import { useCallback, useEffect, useState } from "react";
import { useAuth, useClerk } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { DownloadResourcesBanner } from "../components/DownloadResourcesBanner";
import { VideoPlayer } from "../components/VideoPlayer";
import { fetchCourseModules } from "../lib/courseApi";

const getLessonId = (lesson) =>
  lesson?.id || lesson?._id || lesson?.vimeoVideoId || lesson?.vimeoVideoUri;

const getPosition = (item) => {
  const position = Number(item?.position);
  return Number.isFinite(position) ? position : Number.MAX_SAFE_INTEGER;
};

const sortByPosition = (items = []) =>
  [...items]
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const positionDiff = getPosition(a.item) - getPosition(b.item);
      if (positionDiff !== 0) return positionDiff;
      return a.index - b.index;
    })
    .map(({ item }) => item);

const sortCourseTree = (courses = []) =>
  sortByPosition(courses).map((course) => ({
    ...course,
    modules: course.modules ? sortCourseTree(course.modules) : course.modules,
    subtopics: course.subtopics
      ? sortCourseTree(course.subtopics)
      : course.subtopics,
    lessons: course.lessons ? sortByPosition(course.lessons) : course.lessons,
  }));

const getFirstLesson = (modules) => {
  for (const course of modules) {
    const courseModules = course.modules?.length
      ? course.modules
      : course.subtopics?.length
        ? course.subtopics
        : [course];

    for (const module of courseModules) {
      if (module.lessons?.length) {
        return module.lessons[0];
      }

      for (const subtopic of module.subtopics || []) {
        if (subtopic.lessons?.length) {
          return subtopic.lessons[0];
        }
      }
    }
  }

  return null;
};

export default function EnterStudio() {
  const [modules, setModules] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [status, setStatus] = useState("loading");
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { openSignIn, signOut } = useClerk();
  const navigate = useNavigate();

  const goToDashboard = useCallback(() => {
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      openSignIn();
      return;
    }

    const loadModules = async () => {
      setStatus("loading");

      try {
        const result = await fetchCourseModules(getToken);

        if (result.status === 200) {
          const nextModules = sortCourseTree(result.data?.modules || []);

          setModules(nextModules);
          setActiveLesson(getFirstLesson(nextModules));
          setStatus("ready");
        } else if (result.status === 403) {
          goToDashboard();
        } else if (result.status === 401) {
          await signOut();
          openSignIn();
        } else {
          setStatus("error");
        }
      } catch (err) {
        console.error("Course load failed:", err);
        setStatus("error");
      }
    };

    loadModules();
  }, [isLoaded, isSignedIn, getToken, openSignIn, signOut, goToDashboard]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-400">
        Loading studio...
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-400">
        Unable to load the course right now.
      </div>
    );
  }

  if (!activeLesson) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-400">
        No lessons are available yet.
      </div>
    );
  }

  return (
    <DashboardLayout
      modules={modules}
      activeLessonId={getLessonId(activeLesson)}
      onSelect={(lesson) => setActiveLesson(lesson)}
    >
      <DownloadResourcesBanner />
      <VideoPlayer lesson={activeLesson} />
    </DashboardLayout>
  );
}
